/* eslint max-statements: ["error", {max: 24}] -- this applies for the entire file */

import { useEffect, useRef } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import {
  AnalyticsFieldName,
  RecommendedGroupClassModalAnalyticsPageView,
  RecommendedGroupClassModalAnalyticsSource,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { ModalTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import { useRecommendedGroupClassModalHandlers } from "@/hooks/recommendedGroupClassModal/useRecommendedGroupClassModalHandlers";
import { useModalTracker } from "@/hooks/useModalTracker";
import { useAnalytics } from "@/services/Analytics";
import { useGetClinicalCareTeamQuery } from "@/store/api/clinical";
import { useAppSelector } from "@/store/hooks";
import { useAppUserSelector } from "@/store/reducers/appUser";
import {
  selectCurrentRecommendation,
  useRecommendedGroupClassModalDispatch,
} from "@/store/reducers/modals/recommendedGroupClassModal";
import RecommendedGroupClassModalContent from "@/ui/components/groupClasses/series/RecommendedGroupClassModalContent";
import { Box } from "@/ui/layout";
import ModalLayout from "@/ui/layout/ModalLayout";
import type { ScrollViewRef } from "@/ui/layout/ScrollView";
import { getProfileForPatientExternalId } from "@/utils/profile/patientProfile.util";
import { getDismissalMode } from "@/utils/recommendedGroupClass/storage.util";
import { getPreferredFirstName, getPreferredFullName } from "@/utils/user.util";

const RecommendedGroupClassFAQs = {
  items: [
    "Groups are led by caring, experienced Equip providers",
    "Join in your own way—participate, listen, or just be present (camera off is fine)",
    "Learn practical skills to support your recovery",
    "Feel supported and less alone",
  ],
  title: "What can I expect when I attend a group?",
};

const RecommendedGroupClass = () => {
  const modalScrollViewRef = useRef<ScrollViewRef>(null);
  const appUser = useAppUserSelector();
  const currentRecommendation = useAppSelector(selectCurrentRecommendation);
  const { advance } = useRecommendedGroupClassModalDispatch();

  const { bottom: bottomInset, top: topInset } = useSafeAreaInsets();
  const { setActiveModal } = useModalTracker();

  const { externalId: appUserId, isSupport } = appUser;
  const patientExternalId = currentRecommendation?.patientExternalId;
  const { data: careTeamProviders } = useGetClinicalCareTeamQuery(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- patientExternalId is guaranteed to be set
    patientExternalId!,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- skip is a valid option for useGetClinicalCareTeamQuery
    { skip: isEmpty(patientExternalId) },
  );

  const { trackPageView } = useAnalytics();

  useEffect(() => {
    setActiveModal(ModalPath.RecommendedGroupClass);
    return () => setActiveModal(null);
  }, [setActiveModal]);

  useEffect(() => {
    if (isNil(currentRecommendation)) {
      router.dismiss();
    }
  }, [currentRecommendation]);

  useEffect(() => {
    if (isNil(currentRecommendation)) return;
    if (isNil(modalScrollViewRef.current)) return;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- react native scrollTo API requires the `animated` key
    modalScrollViewRef.current.scrollTo({ animated: true, y: 0 });
  }, [currentRecommendation]);

  useEffect(() => {
    if (!isNil(currentRecommendation)) {
      trackPageView(RecommendedGroupClassModalAnalyticsPageView.Modal, {
        [AnalyticsFieldName.Component]: ModalPath.RecommendedGroupClass,
        [AnalyticsFieldName.Source]: RecommendedGroupClassModalAnalyticsSource,
      });
    }
  }, [currentRecommendation, trackPageView]);

  const dismissalMode = getDismissalMode({
    appUserId,
    patientExternalId,
  });

  const {
    effectiveDismissalMode,
    errorMessage,
    handleAccordionToggle,
    handleDeclineSubmit,
    handleNoneOfTheseTimesPress,
    handleRegisterPress,
    handleRemindMeLaterPress,
    isRegistering,
  } = useRecommendedGroupClassModalHandlers({
    appUserId,
    dismissalMode,
    faqTitle: RecommendedGroupClassFAQs.title,
    onDismissComplete: () => {
      advance();
    },
    onRegisterSuccess: (groupClassExternalId: string) => {
      advance({ registeredGroupClassExternalId: groupClassExternalId });
    },
    patientExternalId,
  });

  if (isNil(currentRecommendation)) {
    return null;
  }

  const { recommendedGroupClass } = currentRecommendation;
  const profileForPatient = getProfileForPatientExternalId({
    appUser,
    patientExternalId: currentRecommendation.patientExternalId,
  });
  const patientFirstName = profileForPatient
    ? getPreferredFirstName({
        chosenName: profileForPatient.chosenName,
        firstName: profileForPatient.firstName,
      })
    : "Patient";

  const firstProvider = careTeamProviders?.[0];
  const therapist = firstProvider
    ? {
        id: firstProvider.providerExternalId,
        name: getPreferredFullName({
          firstName: firstProvider.firstName,
          lastName: firstProvider.lastName,
        }),
      }
    : undefined;

  return (
    <ModalLayout
      isPaddedHorizontally
      renderHeader={() => (
        <Box height={topInset} testID="recommended-group-class" />
      )}
      scrollViewRef={modalScrollViewRef}
      shouldBackdropPressDismiss={false}
    >
      {/* ModalLayout applies no bottom safe-area inset, so on devices whose gesture/nav
          bar overlaps content the bottom "Remind me later" / dismiss link gets clipped;
          add the inset on top of the base padding to lift the content clear of it */}
      <Box flex={1} paddingBottom={36 + bottomInset} paddingTop={24}>
        <RecommendedGroupClassModalContent
          dismissalMode={effectiveDismissalMode}
          errorMessage={errorMessage}
          faqs={RecommendedGroupClassFAQs}
          group={recommendedGroupClass}
          isLoading={isRegistering}
          onAccordionToggle={handleAccordionToggle}
          onDeclineSubmit={handleDeclineSubmit}
          onNoneOfTheseTimesPress={handleNoneOfTheseTimesPress}
          onRegister={({ defaultInstanceId, selectedInstance }) =>
            void handleRegisterPress({
              defaultInstanceId,
              selectedInstance,
            })
          }
          onRegistered={() => advance()}
          onRemindMeLaterPress={handleRemindMeLaterPress}
          patientId={patientExternalId}
          registrantId={appUserId}
          subject={{
            isSupportUser: isSupport,
            patientFirstName,
            therapist,
          }}
          testID={ModalTestID.GroupClassRegistrationModal}
        />
      </Box>
    </ModalLayout>
  );
};

export default RecommendedGroupClass;
